import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

export interface DailyCountdownProps {
  /** Display variant:
   * - 'header': Compact semi-transparent box for desktop header (114x40)
   * - 'floating': Floating card with drop shadow for mobile bottom-left (140x56)
   * - 'badge': Minimal inline pill badge for docks/headers
   */
  variant?: 'header' | 'floating' | 'badge';
  /** Optional callback fired when countdown reaches 00:00:00 (daily store reset) */
  onReset?: () => void;
  /** Optional extra classes */
  className?: string;
}

/** Computes seconds remaining until the next 00:00:00 UTC */
export function getSecondsUntilShopReset(): number {
  const now = new Date();
  const nextReset = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0)
  );
  return Math.max(0, Math.floor((nextReset.getTime() - now.getTime()) / 1000));
}

/** Formats integer seconds into zero-padded HH:MM:SS */
export function formatCountdown(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const h = String(Math.floor(safeSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(safeSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export const DailyCountdown: React.FC<DailyCountdownProps> = ({
  variant = 'header',
  onReset,
  className = '',
}) => {
  const [seconds, setSeconds] = useState<number>(() => getSecondsUntilShopReset());

  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          if (onReset) onReset();
          return getSecondsUntilShopReset();
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onReset]);

  const formattedTime = formatCountdown(seconds);

  if (variant === 'floating') {
    return (
      <div
        className={`fixed bottom-5 left-4 z-40 pointer-events-none filter drop-shadow-[4px_4px_0_#1447e6] ${className}`}
        title="Tiempo para la rotación de tienda"
      >
        <div className="w-[140px] h-14 px-4 flex flex-col justify-center bg-white text-[#06173f] clip-cut select-none pointer-events-auto">
          <small className="font-heading font-bold text-[10px] leading-tight tracking-wider uppercase text-[#1447e6] mb-1 whitespace-nowrap">
            Reinicia en
          </small>
          <div className="font-heading font-bold text-[15px] leading-tight text-[#06173f] flex items-center gap-1.5 tabular-nums whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 text-[#1447e6] shrink-0" />
            <span>{formattedTime}</span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'badge') {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1 bg-[#06173f] border border-blue-400/20 clip-slash text-xs font-heading font-bold text-white ${className}`}
        title="Rotación de la tienda"
      >
        <Clock className="w-3.5 h-3.5 text-[#5ccbff]" />
        <span className="text-[10px] uppercase text-[#5ccbff]">Reinicia:</span>
        <span className="tabular-nums font-mono text-emerald-300">{formattedTime}</span>
      </div>
    );
  }

  // Default 'header' variant: official desktop countdown box
  return (
    <div
      className={`w-28 h-10 px-3 flex flex-col justify-center bg-white/15 clip-slash select-none flex-shrink-0 ${className}`}
      title="Rotación de la tienda"
    >
      <small className="font-heading font-bold text-[10px] leading-none tracking-wider uppercase text-[#5ccbff] mb-0.5 whitespace-nowrap">
        Reinicia en
      </small>
      <div className="font-heading font-bold text-xs leading-none text-white flex items-center gap-1.5 tabular-nums whitespace-nowrap">
        <Clock className="w-3 h-3 text-[#5ccbff] shrink-0" />
        <span>{formattedTime}</span>
      </div>
    </div>
  );
};

export default DailyCountdown;
